import { createAsyncThunk } from "@reduxjs/toolkit"

import { handleApiError } from "../../../Utils/thunkHelpers"
import { getStatisticsAdmin } from "../../BusinessAccountsPage/redux/businessAccounts.thunks"
import * as services from "../services"
import { USERS_SLICE_NAME } from "./users.constants"

/* ------------------------------------------------------------------ *
 * Filters
 * ------------------------------------------------------------------ */

export const getUsersFilterOptions = createAsyncThunk(
  `${USERS_SLICE_NAME}/get_users_filter_options`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getUsersFilterOptions()
    if (!response.ok || !response.data) {
      await handleApiError(dispatch, response)
      return rejectWithValue()
    }
    return response.data
  }
)

/* ------------------------------------------------------------------ *
 * Users list
 * ------------------------------------------------------------------ */

export const getUsers = createAsyncThunk(
  `${USERS_SLICE_NAME}/get_users`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getUsers(params)
    if (!response.ok || !response.data) {
      await handleApiError(dispatch, response)
      return rejectWithValue()
    }
    return response.data
  }
)

// Mirrors the old `apiGeneratorDelete` saga branch (not the generic `apiGeneratorBase`
// one) - success payload is `{ id, message }`, not the raw response body, and a
// successful delete also refreshes the admin dashboard statistics (the saga's
// `GET_STATISTICS_ADMIN_REQUEST` extraAction). `getStatisticsAdmin` reads `test_data` off
// `globalFilters` itself, so it doesn't need to be passed in here.
export const deleteUser = createAsyncThunk(
  `${USERS_SLICE_NAME}/delete_user`,
  async (userId, { dispatch, rejectWithValue }) => {
    const response = await services.deleteUser(userId)
    if (!response.ok || !(response.data || response.status === 204)) {
      await handleApiError(dispatch, response)
      return rejectWithValue()
    }
    dispatch(getStatisticsAdmin())
    return { id: userId, message: response.data?.message?.detail || response.data?.message }
  }
)

// `downloadFile` is commented out on the legacy action config, so this never triggered a
// browser download on its own - it just stores the response body as `exportResult`,
// same as here.
export const getUsersExport = createAsyncThunk(
  `${USERS_SLICE_NAME}/get_users_export`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getUsersExport(params)
    if (!response.ok || !response.data) {
      await handleApiError(dispatch, response)
      return rejectWithValue()
    }
    return response.data
  }
)

/* ------------------------------------------------------------------ *
 * Balance actions
 * ------------------------------------------------------------------ */

export const gift = createAsyncThunk(
  `${USERS_SLICE_NAME}/gift`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.sendGift(params)
    if (!response.ok || !response.data) {
      await handleApiError(dispatch, response)
      return rejectWithValue()
    }
    return response.data
  }
)

export const withdraw = createAsyncThunk(
  `${USERS_SLICE_NAME}/withdraw`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.withDraw(params)
    if (!response.ok || !response.data) {
      await handleApiError(dispatch, response)
      return rejectWithValue()
    }
    return response.data
  }
)

// SwitchToLoyalBillingModal runs its close/notify logic off `.unwrap()` instead of an
// `onSuccess` payload field now.
export const switchToLoyalBilling = createAsyncThunk(
  `${USERS_SLICE_NAME}/switch_to_loyal_billing`,
  async ({ userId, comment }, { dispatch, rejectWithValue }) => {
    const response = await services.switchUserToLoyalBilling({ userId, comment })
    if (!response.ok || !response.data) {
      await handleApiError(dispatch, response)
      return rejectWithValue()
    }
    return response.data
  }
)

/* ------------------------------------------------------------------ *
 * User comments
 * ------------------------------------------------------------------ */

export const getUserInfoComment = createAsyncThunk(
  `${USERS_SLICE_NAME}/get_user_info_comment`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getUserInfoComment(params)
    if (!response.ok || !response.data) {
      await handleApiError(dispatch, response)
      return rejectWithValue()
    }
    return response.data
  }
)

export const getUserBusinessAccountsList = createAsyncThunk(
  `${USERS_SLICE_NAME}/get_user_business_accounts_list`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getUserBusinessAccountsList(params)
    if (!response.ok || !response.data) {
      await handleApiError(dispatch, response)
      return rejectWithValue()
    }
    return response.data
  }
)

export const getUserComment = createAsyncThunk(
  `${USERS_SLICE_NAME}/get_user_comment`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getUserComment(params)
    if (!response.ok || !response.data) {
      await handleApiError(dispatch, response)
      return rejectWithValue()
    }
    return response.data
  }
)

// UsersPage/index.js's mapDispatchToProps strips `onSuccess` off the payload before
// dispatching and runs it off `.unwrap()` instead - the old saga stripped and called it
// the same way (apiGeneratorBaseWithOnSuccess).
export const saveComment = createAsyncThunk(
  `${USERS_SLICE_NAME}/save_comment`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.saveComment(params)
    if (!response.ok || !response.data) {
      await handleApiError(dispatch, response)
      return rejectWithValue()
    }
    return response.data
  }
)

// Same `onSuccess` handling as `saveComment` above - stripped and run off `.unwrap()` by
// the caller (UsersPage/index.js's mapDispatchToProps), not by this thunk.
export const deleteUserComment = createAsyncThunk(
  `${USERS_SLICE_NAME}/delete_user_comment`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.deleteUserComment(params)
    if (!response.ok || !(response.data || response.status === 204)) {
      await handleApiError(dispatch, response)
      return rejectWithValue()
    }
    return { id: params, message: response.data?.message?.detail || response.data?.message }
  }
)

// Same `onSuccess` handling as `saveComment` above - stripped and run off `.unwrap()` by
// the caller (UsersPage/index.js's mapDispatchToProps), not by this thunk.
export const deleteUserCommentAttachment = createAsyncThunk(
  `${USERS_SLICE_NAME}/delete_user_comment_attachment`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.deleteUserCommentAttachment(params)
    if (!response.ok || !(response.data || response.status === 204)) {
      await handleApiError(dispatch, response)
      return rejectWithValue()
    }
    return { id: params, message: response.data?.message?.detail || response.data?.message }
  }
)
